<script>
export default { inheritAttrs: false }
</script>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { Head, usePage } from '@inertiajs/vue3'
import axios from 'axios'
import { Clapperboard, Heart, MessageCircle } from 'lucide-vue-next'
import ClipCommentsSheet from '@/Components/VerticalClips/ClipCommentsSheet.vue'
import ClipNavArrows from '@/Components/VerticalClips/ClipNavArrows.vue'
import ClipOverlay from '@/Components/VerticalClips/ClipOverlay.vue'
import ClipPlayer from '@/Components/VerticalClips/ClipPlayer.vue'
import ClipSharePopup from '@/Components/VerticalClips/ClipSharePopup.vue'
import ClipsLockOverlay from '@/Components/VerticalClips/ClipsLockOverlay.vue'
import ClipsMobileMenu from '@/Components/VerticalClips/ClipsMobileMenu.vue'
import ClipsSidebar from '@/Components/VerticalClips/ClipsSidebar.vue'
import SfwGateModal from '@/Components/VerticalClips/SfwGateModal.vue'
import { useVerticalReelSwipe } from '@/composables/useVerticalReelSwipe'
import { useClipAnalytics } from '@/composables/useClipAnalytics'

const pageProps = defineProps({
    data: Array,
    currentIndex: Number,
    perPage: Number,
    next_page: Number,
    initialClipSlug: String,
    timeoutSettings: Object,
    timeoutLimitReached: Boolean,
    memberHasUnlimitedAccess: Boolean,
    routePrefix: String,
    moreClips: {
        type: Array,
        default: () => [],
    },
    // Ids of the clips in `data` this member has already liked — computed per request,
    // never part of the cached clip payload. Always empty for a guest or on tour.
    likedClipIds: {
        type: Array,
        default: () => [],
    },
    showVideos: {
        type: Boolean,
        default: true,
    },
    // { type, slug, label } on a performer- or fantasy-filtered feed, null otherwise.
    // Retitles the page and keeps every rewritten URL and pagination call inside the filter.
    filter: {
        type: Object,
        default: null,
    },
    // True only on the /clips/sfw routes. Every URL this page builds then stays
    // inside the sfw route family, and the fantasy chips are dropped.
    isSfwFeed: {
        type: Boolean,
        default: false,
    },
    // Guest on the normal feed browsing from an SFW state/country: the feed is blurred,
    // no video is mounted, and SfwGateModal is the only thing left to interact with.
    sfwGated: {
        type: Boolean,
        default: false,
    },
    // { name, headTitle, canonical, ... } on a creator landing (/clips/member/{member} with no
    // clip segment), null everywhere else. Titles the tab after the creator until the first swipe.
    creatorPage: {
        type: Object,
        default: null,
    },
})

const { props } = usePage()
const videos = ref([...(pageProps.data ?? [])])
const currentIndex = ref(pageProps.currentIndex ?? 0)
const timeoutLimitReached = ref(pageProps.timeoutLimitReached ?? false)
const memberHasUnlimitedAccess = ref(pageProps.memberHasUnlimitedAccess ?? false)

const swipeLimit = computed(() => pageProps.timeoutSettings?.swipe?.limit ?? 5)
// A ref, not a computed: the composable rewrites it from each pagination response.
const nextPage = ref(pageProps.next_page)
const isUserLoggedIn = computed(() => !!(props.auth?.user?.id))

const { trackClipFeedViewed, trackClipViewed, trackJoinCtaClicked, trackClipShared, trackClipLiked } =
    useClipAnalytics({ routePrefix: pageProps.routePrefix })

// Fired by ClipsLockOverlay; the single tracking point
// for the locked-content join CTA.
const goToJoinPage = () => {
    const joinUrl = route('www.join.signUp', {
        custom6: 'clipsLockedOverlay',
        ...(currentVideoData.value?.slug ? { custom7: currentVideoData.value.slug } : {}),
    })

    trackJoinCtaClicked('clipsLockedOverlay', currentVideoData.value, joinUrl)

    window.location.href = joinUrl
}

const {
    reelViewportRef,
    videoContainerRef,
    currentVideoPlayerRef,
    currentVideoData,
    nextVideoData,
    previousVideoData,
    windowedSlides,
    setPlayerRef,
    shouldVideoBeLocked,
    shouldAutoplay,
    reelTrackStyle,
    reelViewportStyle,
    onReelSwipeStart,
    onReelSwipeEnd,
    showNextVideo,
    showPreviousVideo,
    publicClipUrl,
    isClipLiked,
    setClipLiked,
} = useVerticalReelSwipe({
    videos,
    currentIndex,
    routePrefix: pageProps.routePrefix,
    filter: pageProps.filter,
    isSfwFeed: pageProps.isSfwFeed,
    initialClipSlug: pageProps.initialClipSlug,
    perPage: pageProps.perPage,
    nextPage,
    likedClipIds: pageProps.likedClipIds,
    swipeLimit,
    memberHasUnlimitedAccess,
    timeoutLimitReached,
})

// Header logo over the video area: the current clip's site logo (resolved server-side by
// site_asset()), falling back to the NA logo. The SFW feed never surfaces a site brand.
const headerSiteLogo = computed(() =>
    pageProps.isSfwFeed ? null : (currentVideoData.value?.site_logo ?? null)
)

// Logo click convention: back to the unfiltered feed's start, same partition (sfw stays sfw).
const homeFeedUrl = computed(() =>
    route(pageProps.routePrefix + '.verticalClips.' + (pageProps.isSfwFeed ? 'sfw.' : '') + 'feed')
)

// Bridges to the current ClipPlayer's exposed surface (resolved from the windowed refs).
const currentVideoProgress = computed(() => currentVideoPlayerRef.value?.videoProgress ?? 0)
const currentVideoIsMuted = computed(() => currentVideoPlayerRef.value?.isMuted ?? true)

const handleVideoToggleMute = () => {
    currentVideoPlayerRef.value?.handleToggleMute()
}

const handleVideoSeek = (percentage) => {
    currentVideoPlayerRef.value?.handleSeek(percentage)
}

// HLS quality override, for testing renditions on local and staging only; production stays on
// ABR, and a page without the shared `app` prop fails closed. Held here rather than per player so
// a pick carries over to the next swiped clip.
const showQualityPicker = props.app?.isProduction === false
const preferredHeight = ref(null)
const currentQualityLevels = computed(() => currentVideoPlayerRef.value?.qualityLevels ?? [])
const currentActiveHeight = computed(() => currentVideoPlayerRef.value?.activeHeight ?? null)

const showSharePopup = ref(false)

const openShareModal = () => {
    showSharePopup.value = true
}

// Only reachable for members now — see openComments, which sends guests to the join
// page instead of opening this.
const showCommentsSheet = ref(false)

// `comments_count` rides inside the cached clip payload, so it can be up to 900s stale.
// Bumping it locally is what makes a member's own comment show on the rail right away.
const onCommentAdded = () => {
    const clip = videos.value[currentIndex.value]

    if (clip) clip.comments_count = (clip.comments_count ?? 0) + 1
}

// The clip's linked scene. Members go straight to the scene page; everyone else lands on
// the join page, since the scene itself is behind the paywall. Both targets are on a
// different host than the feed, so these are real navigations, not Inertia visits.
const goToLinkedScene = () => {
    const clip = currentVideoData.value

    if (!clip?.scene_slug) return

    if (pageProps.routePrefix === 'members2') {
        window.location.href = route('members2.scene.show', clip.scene_slug)

        return
    }

    const joinUrl = route('www.join.signUp', {
        custom6: 'clipsReelsIcon',
        custom7: clip.slug,
    })

    trackJoinCtaClicked('clipsReelsIcon', clip, joinUrl)

    window.location.href = joinUrl
}

// The heart's fill state. Read through the composable so it follows the liked ids that
// arrive with each pagination page, not just the ones the page landed with.
const isCurrentClipLiked = computed(() => isClipLiked(currentVideoData.value?.id))
const likeRequestInFlight = ref(false)

// No count is ever shown, so the only thing a tap has to produce is the icon's state.
// A guest never reaches the endpoint — the heart is a join CTA for them, like every
// other locked action in this feed.
const toggleCurrentClipLike = async () => {
    const clip = currentVideoData.value

    if (!clip) return

    if (!isUserLoggedIn.value) {
        const joinUrl = route('www.join.signUp', {
            custom6: 'clipsLikeIcon',
            custom7: clip.slug,
        })

        trackJoinCtaClicked('clipsLikeIcon', clip, joinUrl)

        window.location.href = joinUrl

        return
    }

    if (likeRequestInFlight.value) return

    const wasLiked = isClipLiked(clip.id)

    // Optimistic flip; the response is only a reconciliation (a second tab could have
    // toggled the same row), and a failed request rolls it straight back.
    setClipLiked(clip.id, !wasLiked)
    likeRequestInFlight.value = true

    try {
        const response = await axios.post(
            route(pageProps.routePrefix + '.verticalClips.like', clip.slug)
        )
        const liked = !!response.data?.liked

        setClipLiked(clip.id, liked)
        trackClipLiked(liked, clip)
    } catch {
        setClipLiked(clip.id, wasLiked)
    } finally {
        likeRequestInFlight.value = false
    }
}

// Guests get the same join-CTA treatment as the like button — the sheet is a
// members-only feature, not a locked preview worth opening for a tour visitor.
const openComments = () => {
    if (!isUserLoggedIn.value) {
        const clip = currentVideoData.value
        const joinUrl = route('www.join.signUp', {
            custom6: 'clipsCommentIcon',
            custom7: clip?.slug,
        })

        trackJoinCtaClicked('clipsCommentIcon', clip, joinUrl)

        window.location.href = joinUrl

        return
    }

    showCommentsSheet.value = true
}

// Clips are publicly visible, so what gets shared must always be the tour URL —
// on members this is deliberately not the same URL as the address bar.
const shareUrl = computed(() => publicClipUrl(currentVideoData.value?.slug))

// --- Client-side meta on swipe -------------------------------------------------
// The server renders the meta for the landing clip; swiping only rewrites the URL
// (history.replaceState in the composable), so the head has to follow along here.
const JSON_LD_ID = 'vertical-clip-video-object'

const setMetaTag = (attr, name, content) => {
    if (!content) return

    let tag = document.querySelector(`meta[${attr}="${name}"]`)

    if (!tag) {
        tag = document.createElement('meta')
        tag.setAttribute(attr, name)
        document.head.appendChild(tag)
    }

    tag.setAttribute('content', content)
}

// On a filtered feed the canonical has to stay inside the filter, matching the URL the
// swipe engine rewrites to; the unfiltered route would advertise a different page.
const feedRouteName = (suffix = '') =>
    pageProps.routePrefix + '.verticalClips.' + (pageProps.isSfwFeed ? 'sfw.' : '') + 'feed' + suffix

const clipUrl = (slug) => {
    if (!slug) return window.location.href

    if (pageProps.filter?.type === 'performer') {
        return route(feedRouteName('.performer'), {
            performer: pageProps.filter.slug,
            clip: slug,
        })
    }

    if (pageProps.filter?.type === 'fantasy') {
        return route(feedRouteName('.fantasy'), {
            fantasy: pageProps.filter.slug,
            clip: slug,
        })
    }

    if (pageProps.filter?.type === 'tag') {
        return route(feedRouteName('.tag'), {
            tag: pageProps.filter.slug,
            clip: slug,
        })
    }

    // No sfw mirror, like the tag feed — a scene feed is never served on that partition.
    if (pageProps.filter?.type === 'scene') {
        return route(feedRouteName('.scene'), {
            scene: pageProps.filter.slug,
            clip: slug,
        })
    }

    // Every member clip. No sfw mirror, like the scene feed.
    if (pageProps.filter?.type === 'members') {
        return route(feedRouteName('.members'), { clip: slug })
    }

    if (pageProps.filter?.type === 'member') {
        return route(feedRouteName('.member'), {
            member: pageProps.filter.slug,
            clip: slug,
        })
    }

    return route(feedRouteName(), { clip: slug })
}

// The tab title is the <Head> below, not document.title: Inertia rewrites the title on every
// head update, through app.js's `${title} - ${appName}`, and appName is clips.blade.php's
// fixed <title> — so this yields the same "<clip> - Vertical Clips | Naughty America" as og:title.
// A creator landing is titled after the creator until the visitor swipes off the first clip.
const hasSwiped = ref(false)

const headTitle = computed(() => {
    if (pageProps.creatorPage && !hasSwiped.value) {
        return pageProps.creatorPage.headTitle
    }

    return currentVideoData.value?.title ?? 'Vertical Clip'
})

// The address bar stays inside the creator's feed (clipUrl), but the canonical is the clip's
// own public URL, matching the server: a member deep link is a duplicate of /clips/{slug}.
// publicClipUrl() can't be used for this — it keeps the member filter in the path.
const canonicalUrl = (slug) => {
    if (slug && pageProps.filter?.type === 'member') {
        return route('www.verticalClips.' + (pageProps.isSfwFeed ? 'sfw.' : '') + 'feed', { clip: slug })
    }

    return clipUrl(slug)
}

const updateMetaTags = () => {
    const clip = videos.value[currentIndex.value]

    if (!clip) return

    const title = `${clip.title ?? 'Vertical Clip'} - Vertical Clips | Naughty America`
    const description = clip.description || clip.title || 'Watch vertical clips on Naughty America.'
    const image = clip.og_image || clip.start_card_image || ''
    const url = canonicalUrl(clip.slug)

    setMetaTag('name', 'description', description)

    let canonicalLink = document.querySelector('link[rel="canonical"]')

    if (!canonicalLink) {
        canonicalLink = document.createElement('link')
        canonicalLink.rel = 'canonical'
        document.head.appendChild(canonicalLink)
    }

    canonicalLink.href = url

    setMetaTag('property', 'og:title', title)
    setMetaTag('property', 'og:description', description)
    setMetaTag('property', 'og:url', url)
    setMetaTag('property', 'og:image', image)
    setMetaTag('name', 'twitter:card', image ? 'summary_large_image' : 'summary')
    setMetaTag('name', 'twitter:title', title)
    setMetaTag('name', 'twitter:description', description)
    setMetaTag('name', 'twitter:image', image)

    let structuredData = document.getElementById(JSON_LD_ID)

    if (!structuredData) {
        structuredData = document.createElement('script')
        structuredData.type = 'application/ld+json'
        structuredData.id = JSON_LD_ID
        document.head.appendChild(structuredData)
    }

    structuredData.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'VideoObject',
        name: clip.title ?? '',
        description,
        thumbnailUrl: image,
        uploadDate: clip.created_at ?? undefined,
    })
}

const cleanupMetaTags = () => {
    document.getElementById(JSON_LD_ID)?.remove()
}

// --- Analytics -----------------------------------------------------------------
// A clip counts as viewed once per page load, not once per swipe-back, so swiping
// up and down a short feed can't inflate the counts.
const viewedClipIds = new Set()

const trackCurrentClipViewed = () => {
    const clip = videos.value[currentIndex.value]

    if (!clip || viewedClipIds.has(clip.id)) return

    viewedClipIds.add(clip.id)

    trackClipViewed(clip, {
        position: currentIndex.value,
        filter_type: pageProps.filter?.type ?? null,
        filter_slug: pageProps.filter?.slug ?? null,
        is_member: isUserLoggedIn.value,
    })
}

// ClipSharePopup announces the chosen method on the window; the clip it applies to is
// whichever one is active when the share happens.
const onSharePopupUsed = (event) => {
    trackClipShared(event.detail?.method ?? null, currentVideoData.value)
}

watch(currentIndex, () => {
    hasSwiped.value = true
    updateMetaTags()
    trackCurrentClipViewed()
})

onMounted(() => {
    window.scrollTo(0, 0)

    document.body.classList.add('clips-feed-page')
    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'

    trackClipFeedViewed({
        entry_clip_slug: pageProps.initialClipSlug,
        filter_type: pageProps.filter?.type ?? null,
        filter_slug: pageProps.filter?.slug ?? null,
        is_member: isUserLoggedIn.value,
    })

    // The watch above only fires on change, so the landing clip needs its own call.
    trackCurrentClipViewed()

    window.addEventListener('share-popup:used', onSharePopupUsed)
})

onBeforeUnmount(() => {
    cleanupMetaTags()

    window.removeEventListener('share-popup:used', onSharePopupUsed)

    document.body.classList.remove('clips-feed-page')
    document.body.style.overflow = ''
    document.documentElement.style.overflow = ''
})
</script>

<template>
    <Head :title="headTitle" />

    <div class="flex h-[100dvh] overflow-hidden bg-black text-white">
        <div class="flex flex-1 max-w-[1522px] mx-auto w-full">
            <div
                id="left-side-mainVideo"
                ref="videoContainerRef"
                class="relative flex w-full flex-1 justify-center items-start bg-black outline-none"
            >
                <div
                    class="absolute left-0 right-0 top-0 z-30 flex items-center justify-between px-4 py-3"
                    :style="{ width: reelViewportStyle.width, margin: '0 auto' }"
                >
                    <a :href="homeFeedUrl" aria-label="Back to clips feed">
                        <img
                            v-if="headerSiteLogo"
                            :src="headerSiteLogo"
                            :alt="currentVideoData?.site_name ?? ''"
                            class="max-h-6 w-auto max-w-[180px] object-contain"
                        />
                        <picture v-else>
                            <source
                                srcset="https://sm.naughtycdn.com/images/newTour/nalogo-1x.webp 1x, https://sm.naughtycdn.com/images/newTour/nalogo-2x.webp 2x, https://sm.naughtycdn.com/images/newTour/nalogo-3x.webp 3x"
                                type="image/webp"
                            />
                            <img
                                src="https://sm.naughtycdn.com/images/newTour/nalogo-1x.png"
                                srcset="https://sm.naughtycdn.com/images/newTour/nalogo-1x.png 1x, https://sm.naughtycdn.com/images/newTour/nalogo-2x.png 2x, https://sm.naughtycdn.com/images/newTour/nalogo-3x.png 3x"
                                class="max-h-6 w-auto max-w-[180px] object-contain"
                                alt="Naughty America"
                            />
                        </picture>
                    </a>

                    <h1 v-if="filter?.label && filter.type === 'member'" class="text-sm font-semibold">{{ filter.label }}</h1>
                    <p v-else-if="filter?.label" class="text-sm font-semibold">{{ filter.label }}</p>

                    <!-- Mobile only: on desktop the kebab in the sidebar top bar is the only menu.
                         The drawer brings its own trigger; its panel is teleported to <body> and
                         carries its own close button, since this header is a stacking context
                         (absolute z-30) and is therefore blurred by the drawer's own backdrop. -->
                    <div class="lg:hidden">
                        <ClipsMobileMenu
                            :isUserLoggedIn="isUserLoggedIn"
                            :routePrefix="routePrefix"
                            :isSfwFeed="pageProps.isSfwFeed"
:currentClip="currentVideoData"
                        />
                    </div>

                    <div class="hidden lg:block h-10 w-10" />
                </div>

                <div
                    ref="reelViewportRef"
                    class="reel-viewport relative touch-none select-none overflow-hidden"
                    :class="{ 'sfw-gate-blur': sfwGated }"
                    :style="reelViewportStyle"
                    @touchstart="onReelSwipeStart"
                    @touchend="onReelSwipeEnd"
                >
                    <!-- Windowed slides keyed by clip id: players persist as their position
                         shifts, so buffered media survives a swipe. -->
                    <div class="reel-track" :style="reelTrackStyle">
                        <div
                            v-for="slide in windowedSlides"
                            :key="slide.video.id"
                            class="reel-slide shrink-0"
                        >
                            <ClipPlayer
                                :ref="(el) => setPlayerRef(slide.video.id, el)"
                                :videoData="slide.video"
                                :routePrefix="routePrefix"
                                :shouldAutoplay="slide.isCurrent && shouldAutoplay(slide.index)"
                                :shouldVideoBeLocked="shouldVideoBeLocked(slide.index)"
                                :preloadMode="slide.preloadMode"
                                :prefetchHls="slide.isNext"
                                :preferredHeight="showQualityPicker ? preferredHeight : null"
                                :renderVideo="pageProps.showVideos && !pageProps.sfwGated"
                            />
                        </div>
                    </div>

                    <!-- Fixed fades - always visible during swipe (not tied to sliding ClipPlayers) -->
                    <div class="pointer-events-none absolute inset-0 z-10">
                        <div class="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/40 via-black/10 to-transparent" />
                        <div class="absolute inset-x-0 bottom-0 h-44 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                    </div>
                    <div class="absolute inset-0 bg-gradient-to-b from-black/35 via-black/0 to-black/65 z-[15] pointer-events-none" />

                    <!-- Persistent sibling of .reel-track: the rail must not slide with the video -->
                    <div class="absolute right-3 bottom-32 z-[49] flex flex-col items-center gap-6">
                        <button
                            v-if="currentVideoData?.scene_slug"
                            type="button"
                            @click="goToLinkedScene"
                            class="flex flex-col items-center gap-1.5 group active:scale-95 transition-transform"
                            aria-label="Full Scene"
                        >
                            <div class="w-12 h-12 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center hover:bg-white/15 transition-colors shadow-lg border border-white/10">
                                <Clapperboard class="w-5 h-5 text-white" />
                            </div>
                        </button>

                        <!-- Always rendered, unlike "Full Scene": every clip can be liked.
                             No label under it — there is no count to show. -->
                        <button
                            type="button"
                            @click="toggleCurrentClipLike"
                            class="flex flex-col items-center gap-1.5 group active:scale-95 transition-transform"
                            :aria-label="isCurrentClipLiked ? 'Unlike' : 'Like'"
                            :aria-pressed="isCurrentClipLiked"
                        >
                            <div class="w-12 h-12 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center hover:bg-white/15 transition-colors shadow-lg border border-white/10">
                                <Heart
                                    class="w-5 h-5 transition-colors"
                                    :class="isCurrentClipLiked ? 'text-red-500' : 'text-white'"
                                    :fill="isCurrentClipLiked ? 'currentColor' : 'none'"
                                />
                            </div>
                        </button>

                        <!-- Count rides the puck as a corner badge, hidden at 0: matching
                             VideoControls.vue's commentCount convention from Reels. -->
                        <button
                            type="button"
                            @click="openComments"
                            class="flex flex-col items-center gap-1.5 group active:scale-95 transition-transform"
                            aria-label="Comments"
                        >
                            <div class="relative w-12 h-12 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center hover:bg-white/15 transition-colors shadow-lg border border-white/10">
                                <MessageCircle class="w-5 h-5 text-white" />
                                <span
                                    v-if="(currentVideoData?.comments_count ?? 0) > 0"
                                    class="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-white text-gray-900 text-[10px] font-semibold leading-none flex items-center justify-center shadow"
                                >{{ currentVideoData.comments_count }}</span>
                            </div>
                        </button>

                        <button
                            type="button"
                            @click="openShareModal"
                            class="flex flex-col items-center gap-1.5 group active:scale-95 transition-transform"
                            aria-label="Share"
                        >
                            <div class="w-12 h-12 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center hover:bg-white/15 transition-colors shadow-lg border border-white/10">
                                <svg class="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92c0-1.61-1.31-2.92-2.92-2.92z" />
                                </svg>
                            </div>
                        </button>
                    </div>

                    <!-- Persistent sibling of .reel-track: the caption must not slide with the video -->
                    <ClipOverlay
                        v-if="currentVideoData && !shouldVideoBeLocked(currentIndex)"
                        :videoData="videos[currentIndex]"
                        :routePrefix="routePrefix"
                        :isSfwFeed="isSfwFeed"
                        :progress="currentVideoProgress"
                        :isMuted="currentVideoIsMuted"
                        :qualityLevels="showQualityPicker ? currentQualityLevels : []"
                        :activeHeight="currentActiveHeight"
                        :preferredHeight="preferredHeight"
                        @toggleMute="handleVideoToggleMute"
                        @selectQuality="(height) => (preferredHeight = height)"
                        @seek="handleVideoSeek"
                        @share="openShareModal"
                    />

                    <!-- Lock card sits above ClipOverlay (z-40) and share rail (z-49); cannot live
                         inside ClipPlayer because .reel-track's transform traps inner z-index. -->
                    <ClipsLockOverlay
                        v-if="shouldVideoBeLocked(currentIndex)"
                        @joinClick="goToJoinPage"
                    />
                </div>

                <ClipNavArrows
                    :canGoPrevious="!!previousVideoData"
                    :canGoNext="!!nextVideoData"
                    @previous="showPreviousVideo"
                    @next="showNextVideo"
                />
            </div>

            <ClipsSidebar
                :isUserLoggedIn="isUserLoggedIn"
                :routePrefix="routePrefix"
                :moreClips="moreClips"
                :currentClip="currentVideoData"
                :filter="filter"
                :isSfwFeed="isSfwFeed"
            />
        </div>

        <ClipSharePopup v-model:open="showSharePopup" :share-url="shareUrl" />

        <ClipCommentsSheet
            :isOpen="showCommentsSheet"
            :clip="currentVideoData"
            :routePrefix="routePrefix"
            :isUserLoggedIn="isUserLoggedIn"
            @close="showCommentsSheet = false"
            @commentAdded="onCommentAdded"
        />

        <!-- Non-dismissable: a guest in an SFW region has no way past it on this feed. -->
        <SfwGateModal v-if="sfwGated" />
    </div>
</template>

<style scoped>
.reel-track {
    display: flex;
    flex-direction: column;
}

.reel-slide {
    height: 100%;
}

.reel-viewport :deep(.spinner) {
    display: none;
}

/* Sits on the viewport, not the video element: the caption, chips, share rail and start
   card all have to go behind the blur, not just the player. */
.sfw-gate-blur {
    filter: blur(24px);
    pointer-events: none;
}
</style>

<style>
body.clips-feed-page {
    overflow: hidden;
    /* touch-action: pan-y is what actually stops Chrome for iOS from
       recognizing its native edge swipe-to-go-back gesture here —
       overscroll-behavior-x only governs scroll chaining, not the
       edge-pan gesture recognizer (see FeedV2.vue for the full note). */
    overscroll-behavior-x: none;
    touch-action: pan-y;
}

.reel-viewport {
    overscroll-behavior-x: none;
    touch-action: pan-y;
    user-select: none;
}
</style>
